import type {JourneyParticipationADto} from '@cohort/admin-schemas/journeyParticipation';
import JourneyParticipationStatusBadge from '@cohort/merchants/components/campaigns/journeys/JourneyParticipationStatusBadge';
import JourneyParticipationStepper from '@cohort/merchants/components/campaigns/journeys/JourneyParticipationStepper';
import {Progress} from '@cohort/merchants/components/Progress';
import CampaignReferenceComponent from '@cohort/merchants/components/references/CampaignReferenceComponent';
import Table from '@cohort/merchants/components/Table';
import {formatDate} from '@cohort/shared/utils/format';
import {useAutoAnimate} from '@formkit/auto-animate/react';
import {Disclosure} from '@headlessui/react';
import {CaretDown, CaretUp} from '@phosphor-icons/react';
import {Fragment} from 'react';
import {useTranslation} from 'react-i18next';

type UserJourneysTableProps = {
  isLoading: boolean;
  journeyParticipations: JourneyParticipationADto[];
};

const UserJourneysTable: React.FC<UserJourneysTableProps> = ({
  isLoading,
  journeyParticipations,
}) => {
  const {t} = useTranslation('pages', {
    keyPrefix: 'users.user.journeys.userJourneysTable',
  });
  const [parent] = useAutoAnimate();

  return (
    <Table
      headers={[
        {label: t('tableHeaderJourney')},
        {label: t('tableHeaderJoinedAt')},
        {label: t('tableHeaderStatus')},
        {label: t('tableHeaderCompletedSteps')},
        {label: '', width: '200px'},
      ]}
      isLoading={isLoading}
      emptyState={{
        isEmpty: journeyParticipations.length === 0,
        title: t('titleEmpty'),
        description: t('descriptionEmpty'),
      }}
    >
      {journeyParticipations.map((journeyParticipation: JourneyParticipationADto) => {
        const countCompletedSteps = journeyParticipation.stepCompletionAttempts.filter(
          attempt => attempt.success
        ).length;
        const countTotalSteps = journeyParticipation.journeySteps.length;

        return (
          <Disclosure key={journeyParticipation.id}>
            {({open}) => (
              <Fragment>
                <tr key={journeyParticipation.id} className="last:pr-0! *:py-3 *:pr-4">
                  <td>
                    <CampaignReferenceComponent
                      internalName={journeyParticipation.campaign.internalName}
                      campaignId={journeyParticipation.campaign.id}
                      imageFileKey={journeyParticipation.campaign.imageFileKey}
                    />
                  </td>
                  <td className="text-slate-500">{formatDate(journeyParticipation.createdAt)}</td>
                  <td>
                    <JourneyParticipationStatusBadge status={journeyParticipation.status} />
                  </td>
                  <td className="text-slate-500">
                    <div className="flex w-[150px] items-center space-x-2">
                      <Progress
                        value={
                          countTotalSteps === 0 ? 0 : (countCompletedSteps / countTotalSteps) * 100
                        }
                      />
                      <p className="font-normal text-slate-700">
                        {countCompletedSteps}/{countTotalSteps}
                      </p>
                    </div>
                  </td>
                  <td>
                    <Disclosure.Button>
                      <div className="flex items-center space-x-2 text-sm font-medium text-primary">
                        {open ? (
                          <Fragment>
                            {t('buttonHideDetails')}
                            <CaretUp className="h-4 w-4" />
                          </Fragment>
                        ) : (
                          <Fragment>
                            <p>{t('buttonShowDetails')}</p>
                            <CaretDown className="h-4 w-4" />
                          </Fragment>
                        )}
                      </div>
                    </Disclosure.Button>
                  </td>
                </tr>

                <tr ref={parent}>
                  <Disclosure.Panel as="td" colSpan={5}>
                    <JourneyParticipationStepper journeyParticipation={journeyParticipation} />
                  </Disclosure.Panel>
                </tr>
              </Fragment>
            )}
          </Disclosure>
        );
      })}
    </Table>
  );
};
export default UserJourneysTable;
